import type API from "@/Api/API";
import type { Duplicated } from "@/types/duplication";
import type {
	AudioProcessingOperation,
	CreateOrUpdateMusicDto,
	CreateOrUpdatePlaylistDto,
	Genre,
	Music,
	Playlist,
	PlaylistMusic,
	Tag,
} from "@/types/library";
import type {
	CreateOrUpdatePlaylistGeneratorItemDto,
	PlaylistGeneratorItem,
} from "@/types/playlist-editor";

export default class Library {
	api: API;

	constructor(api: API) {
		this.api = api;
	}

	async getLibraryMusics(): Promise<Music[]> {
		return this.api
			.processRequest("GET", "/library/musics")
			.then((res) => res.data);
	}

	async getLibraryPlaylists(): Promise<Playlist[]> {
		return this.api
			.processRequest("GET", "/library/playlists")
			.then((res) => res.data);
	}

	async getLibraryPlaylist(id: number): Promise<Playlist> {
		return this.api
			.processRequest("GET", `/library/playlists/${id}`)
			.then((res) => res.data);
	}

	async addLibraryPlaylist(
		dto: CreateOrUpdatePlaylistDto
	): Promise<Playlist> {
		return this.api
			.processRequest("POST", "/library/playlists", {
				data: dto,
			})
			.then((res) => res.data);
	}

	async updateLibraryPlaylist(
		id: number,
		dto: CreateOrUpdatePlaylistDto
	): Promise<Playlist> {
		return this.api
			.processRequest("PUT", `/library/playlists/${id}`, {
				data: dto,
			})
			.then((res) => res.data);
	}

	async checkPlaylistUsage(id: number): Promise<{
		scenes: Array<{ id: number; name: string }>;
	}> {
		return this.api
			.processRequest("GET", `/library/playlists/${id}/usage`)
			.then((res) => res.data);
	}

	async deleteLibraryPlaylist(id: number, force = false): Promise<void> {
		await this.api.processRequest(
			"DELETE",
			`/library/playlists/${id}${force ? "?force=true" : ""}`
		);
	}

	async addPlaylistMusics(id: number, musics: Music[]): Promise<Playlist> {
		return this.api
			.processRequest("PUT", `/library/playlists/${id}/musics`, {
				data: { musics: musics.map((m) => m.id) },
			})
			.then((res) => res.data);
	}

	async deletePlaylistMusic(
		id: number,
		playlistMusic: PlaylistMusic
	): Promise<Playlist> {
		return this.api
			.processRequest(
				"DELETE",
				`/library/playlists/${id}/musics/${playlistMusic.id}`
			)
			.then((res) => res.data);
	}

	async swapPlaylistMusics(
		id: number,
		oldOrder: number,
		newOrder: number
	): Promise<Playlist> {
		return this.api
			.processRequest("PUT", `/library/playlists/${id}/musics/reorder`, {
				data: { oldOrder, newOrder },
			})
			.then((res) => res.data);
	}

	async getLibraryPlaylistGeneratorItems(
		id: number
	): Promise<PlaylistGeneratorItem[]> {
		return this.api
			.processRequest("GET", `/library/playlists/${id}/generator`)
			.then((res) => res.data);
	}

	async addLibraryPlaylistGeneratorItem(
		id: number,
		dto: CreateOrUpdatePlaylistGeneratorItemDto
	): Promise<PlaylistGeneratorItem> {
		return this.api
			.processRequest("POST", `/library/playlists/${id}/generator`, {
				data: dto,
			})
			.then((res) => res.data);
	}

	async updateLibraryPlaylistGeneratorItem(
		id: number,
		itemId: number,
		dto: CreateOrUpdatePlaylistGeneratorItemDto
	): Promise<PlaylistGeneratorItem> {
		return this.api
			.processRequest(
				"PUT",
				`/library/playlists/${id}/generator/${itemId}`,
				{
					data: dto,
				}
			)
			.then((res) => res.data);
	}

	async deleteLibraryPlaylistGeneratorItem(
		id: number,
		itemId: number
	): Promise<void> {
		await this.api.processRequest(
			"DELETE",
			`/library/playlists/${id}/generator/${itemId}`
		);
	}

	async swapLibraryPlaylistGeneratorItems(
		id: number,
		item1: number,
		item2: number
	): Promise<void> {
		return this.api
			.processRequest("PUT", `/library/playlists/${id}/generator/swap`, {
				data: { item1, item2 },
			})
			.then((res) => res.data);
	}

	async generatePlaylist(
		id: number,
		seed: number = Math.floor(Math.random() * 1000000)
	): Promise<void> {
		return this.api
			.processRequest("POST", `/library/playlists/${id}/generate`, {
				data: { seed },
			})
			.then((res) => res.data);
	}

	async getLibraryTags(): Promise<Tag[]> {
		return this.api
			.processRequest("GET", "/library/tags")
			.then((res) => res.data);
	}

	async addLibraryTag(tag: Tag): Promise<Tag> {
		return this.api
			.processRequest("POST", "/library/tags", {
				data: tag,
			})
			.then((res) => res.data);
	}

	async getLibraryGenres(): Promise<Genre[]> {
		return this.api
			.processRequest("GET", "/library/genres")
			.then((res) => res.data);
	}

	async addLibraryGenre(genre: Genre): Promise<Genre> {
		return this.api
			.processRequest("POST", "/library/genres", {
				data: genre,
			})
			.then((res) => res.data);
	}

	async addLibraryMusic(
		dto: CreateOrUpdateMusicDto
	): Promise<{ music: Music; signedUrl: string }> {
		return this.api
			.processRequest("POST", "/library/musics", {
				data: {
					fileName: dto.file?.name,
					title: dto.title,
					artist: dto.artist,
					genreId: dto.selectedGenre,
					tags: dto.selectedTags,
					bpm: dto.bpm,
					duration: dto.duration,
				},
			})
			.then((res) => res.data);
	}

	async updateLibraryMusic(
		id: number,
		dto: CreateOrUpdateMusicDto
	): Promise<{ music: Music; signedUrl: string }> {
		return this.api
			.processRequest("PUT", `/library/musics/${id}`, {
				data: {
					fileName: dto.file?.name,
					title: dto.title,
					artist: dto.artist,
					bpm: dto.bpm,
					duration: dto.duration,
					genreId: dto.selectedGenre,
					tags: dto.selectedTags,
					timestamps: dto.timestamps,
				},
			})
			.then((res) => res.data);
	}

	async updateLibraryMusicTimestamps(
		id: number,
		data: {
			timestamps: Array<{ id: number; time: number; label: string }>;
			startTimestamp?: number;
			endTimestamp?: number;
		}
	): Promise<Music> {
		return this.api
			.processRequest("PUT", `/library/musics/${id}/timestamps`, {
				data,
			})
			.then((res) => res.data);
	}

	async completeMusicUpload(
		id: number,
		operations: AudioProcessingOperation[]
	): Promise<Music> {
		return this.api
			.processRequest("POST", `/library/musics/${id}/complete`, {
				data: { operations },
			})
			.then((res) => res.data);
	}

	// Raw fetch on purpose: the PUT goes straight to the GCS signed URL and
	// must NOT carry the API Authorization header.
	/* eslint-disable-next-line class-methods-use-this */
	async uploadMusicFile(signedUrl: string, file: File): Promise<Response> {
		return fetch(signedUrl, {
			method: "PUT",
			headers: {
				"Content-Type": file.type,
			},
			body: file,
		});
	}

	async getLibraryMusicStream(id: number): Promise<string> {
		return this.api
			.processRequest("GET", `/library/musics/${id}/stream`)
			.then((res) => res.data);
	}

	async checkMusicUsage(id: number): Promise<{
		playlists: Array<{ id: number; title: string }>;
		scenes: Array<{ id: number; name: string }>;
	}> {
		return this.api
			.processRequest("GET", `/library/musics/${id}/usage`)
			.then((res) => res.data);
	}

	async deleteLibraryMusic(id: number): Promise<void> {
		await this.api.processRequest("DELETE", `/library/musics/${id}`);
	}

	async duplicateLibraryMusic(
		id: number,
		targetUuid?: string
	): Promise<Duplicated<Music>> {
		return this.api
			.processRequest("POST", `/library/musics/${id}/duplicate`, {
				data: { targetUuid },
			})
			.then((res) => res.data);
	}

	async duplicateLibraryPlaylist(
		id: number,
		targetUuid?: string
	): Promise<Duplicated<Playlist>> {
		return this.api
			.processRequest("POST", `/library/playlists/${id}/duplicate`, {
				data: { targetUuid },
			})
			.then((res) => res.data);
	}
}
